import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Advisor, AdvisorReview } from '@/types'

import Link from 'next/link'
import {
    ArrowLeft,
    Languages,
    MapPin,
    Star,
    Video
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Landscape } from '@/components/generals/landscape'
import { PillButton } from '@/components/generals/pill-button'
import { RatingChip } from '@/components/generals/rating-chip'
import { Reveal } from '@/components/generals/reveal'
import { RichText } from '@/components/generals/rich-text'
import { SplitHeading } from '@/components/generals/split-heading'
import { TextLink } from '@/components/generals/text-link'
import { WashedPortrait } from '@/components/generals/washed-portrait'
import { AdvisorCard } from '@/components/landing/advisors/advisor-card'
import { BookingPanel } from '@/components/landing/advisors/booking-panel'
import { NextAvailable } from '@/components/landing/advisors/next-available'
import { getAdvisorBySlug, getAdvisorReviews, getAdvisors, getAdvisorSlugs } from '@/lib/advisors'
import { LANDSCAPES } from '@/lib/visual'

interface AdvisorPageProps {
    advisor: Advisor
    reviews: Array<AdvisorReview>
    others: Array<Advisor>
}

const AdvisorPage: FC<AdvisorPageProps> = ({ advisor, reviews, others }): ReactNode => {
    const first = advisor.name.split(' ')[0]

    return (
        <Layout>
            <Seo
                title={advisor.name}
                description={`${advisor.name}, ${advisor.title}. ${advisor.shortBio} Your first 30 minute call with ${first} at Verdane is free.`}
                ogImage={advisor.portrait ?? undefined}
            />

            <section aria-labelledby='advisor-name' className='relative isolate overflow-hidden pb-20 pt-32 sm:pt-40'>
                <div aria-hidden='true' className='absolute inset-x-0 top-0 -z-10 h-[680px] opacity-70'>
                    <Landscape src={LANDSCAPES.meadowBlur} blur='md' fade='none' />
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0.3)_35%,#000_100%)]' />
                </div>

                <div className='shell'>
                    <Reveal trigger='mount' y={10}>
                        <Link href='/advisors' className='inline-flex items-center gap-2 text-sm text-cream/60 transition-colors hover:text-cream'>
                            <ArrowLeft aria-hidden='true' className='size-4' />
                            Back to all advisors
                        </Link>
                    </Reveal>

                    <div className='mt-10 grid items-end gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16'>
                        <div className='motion-rise [--rise-delay:350ms]'>
                            <div className='grain relative isolate mx-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden rounded-[36px] bg-ink shadow-[0_60px_120px_-50px_rgba(226,243,122,0.3)] lg:mx-0'>
                                {advisor.portrait && (
                                    <WashedPortrait
                                        src={advisor.portrait}
                                        alt={advisor.portraitAlt}
                                        wash={advisor.washColor}
                                        intensity='medium'
                                        sizes='(min-width: 1024px) 45vw, 92vw'
                                        priority
                                    />
                                )}
                                <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0)_30%,rgba(0,0,0,0)_60%,rgba(0,0,0,0.7)_100%)]' />
                                <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                                <div className='absolute inset-x-5 top-5 z-10 flex flex-wrap items-center gap-2'>
                                    <RatingChip rating={advisor.rating} reviews={advisor.reviewCount} />

                                    <span className='glass rounded-full px-3 py-1 text-xs text-cream'>
                                        {advisor.yearsExperience} years advising
                                    </span>
                                </div>

                                <div className='absolute inset-x-5 bottom-5 z-10'>
                                    <p className='glass inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs text-cream'>
                                        <span aria-hidden='true' className='size-1.5 rounded-full bg-citron shadow-[0_0_10px_rgba(226,243,122,0.8)]' />
                                        Next opening: <NextAvailable advisor={advisor} />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className='pb-2'>
                            <Eyebrow align='left'>
                                {advisor.specialty}
                            </Eyebrow>

                            <SplitHeading
                                as='h1'
                                id='advisor-name'
                                trigger='mount'
                                delay={200}
                                text={`*${advisor.name}*`}
                                serifDelay={0}
                                className='mt-6 text-[3.2rem] font-normal leading-[0.95] text-cream sm:text-7xl lg:text-[5.4rem]'
                            />

                            <Reveal trigger='mount' delay={500}>
                                <p className='mt-4 text-base text-cream/60'>
                                    {advisor.title}
                                </p>

                                <p className='mt-6 max-w-xl text-xl leading-relaxed tracking-[-0.01em] text-cream/85'>
                                    {advisor.shortBio}
                                </p>

                                <ul className='mt-7 flex flex-wrap gap-2'>
                                    {advisor.credentials.map(credential => (
                                        <li key={credential} className='rounded-full bg-white/[0.06] px-3 py-1.5 text-xs text-cream/80 ring-1 ring-white/10'>
                                            {credential}
                                        </li>
                                    ))}
                                </ul>

                                <dl className='mt-7 grid gap-3 text-sm text-cream/65 sm:grid-cols-3'>
                                    <div className='flex items-center gap-2'>
                                        <dt>
                                            <MapPin aria-hidden='true' className='size-4 text-citron' />
                                            <span className='sr-only'>Based in</span>
                                        </dt>
                                        <dd>
                                            {advisor.location}
                                        </dd>
                                    </div>

                                    <div className='flex items-center gap-2'>
                                        <dt>
                                            <Languages aria-hidden='true' className='size-4 text-citron' />
                                            <span className='sr-only'>Languages spoken</span>
                                        </dt>
                                        <dd>
                                            {advisor.languages.join(', ')}
                                        </dd>
                                    </div>

                                    <div className='flex items-center gap-2'>
                                        <dt>
                                            <Video aria-hidden='true' className='size-4 text-citron' />
                                            <span className='sr-only'>Ways to meet</span>
                                        </dt>
                                        <dd>
                                            {advisor.meetingFormats.join(', ')}
                                        </dd>
                                    </div>
                                </dl>

                                <div className='mt-9 flex flex-wrap items-center gap-6'>
                                    <PillButton href='#book' size='md'>
                                        Book time with {first}
                                    </PillButton>

                                    <TextLink href='/ai-intelligence#demo'>
                                        Start with the AI advisor
                                    </TextLink>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label={`More about ${advisor.name}`} className='relative pb-28'>
                <div className='shell grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,430px)] lg:gap-16'>
                    <div className='space-y-20'>
                        {advisor.bio && (
                            <Reveal>
                                <Eyebrow align='left' animate={false}>
                                    Meet {first}
                                </Eyebrow>

                                <RichText html={advisor.bio} className='mt-6 max-w-2xl text-lg leading-relaxed text-cream/75' />
                            </Reveal>
                        )}

                        {advisor.approach.length > 0 && (
                            <div>
                                <Eyebrow align='left'>
                                    Working together
                                </Eyebrow>

                                <ol className='mt-6 grid gap-3'>
                                    {advisor.approach.map((step, index) => (
                                        <Reveal as='li' key={step} delay={index * 120} className='glass flex gap-5 rounded-[24px] p-5 sm:p-6'>
                                            <span className='font-serif text-3xl italic leading-none text-citron'>
                                                0{index + 1}
                                            </span>

                                            <p className='pt-1 text-[15px] leading-relaxed text-cream/80'>
                                                {step}
                                            </p>
                                        </Reveal>
                                    ))}
                                </ol>
                            </div>
                        )}

                        {advisor.focusAreas.length > 0 && (
                            <div>
                                <Eyebrow align='left'>
                                    Where they help most
                                </Eyebrow>

                                <Reveal as='ul' className='mt-6 flex flex-wrap gap-2.5'>
                                    {advisor.focusAreas.map(area => (
                                        <li key={area} className='rounded-full bg-white/[0.05] px-4 py-2 text-sm text-cream/85 ring-1 ring-white/10'>
                                            {area}
                                        </li>
                                    ))}
                                </Reveal>
                            </div>
                        )}

                        {reviews.length > 0 && (
                            <div>
                                <Eyebrow align='left'>
                                    What clients say
                                </Eyebrow>

                                <ul className='mt-6 grid gap-4 sm:grid-cols-2'>
                                    {reviews.map((review, index) => (
                                        <Reveal as='li' key={review.id} delay={(index % 2) * 120} className={index === 0 ? 'sm:col-span-2' : undefined}>
                                            <figure className='grain relative h-full overflow-hidden rounded-[26px] bg-white/[0.04] p-6 ring-1 ring-white/10'>
                                                <div className='flex gap-0.5' aria-label={`${review.rating} out of 5 stars`}>
                                                    {Array.from({ length: 5 }, (_, star) => (
                                                        <Star key={star} aria-hidden='true' className={star < review.rating ? 'size-3.5 fill-citron text-citron' : 'size-3.5 text-cream/25'} />
                                                    ))}
                                                </div>

                                                <blockquote className={index === 0 ? 'mt-4 text-2xl leading-snug tracking-[-0.02em] text-cream' : 'mt-4 text-[15px] leading-relaxed text-cream/85'}>
                                                    &ldquo;{review.quote}&rdquo;
                                                </blockquote>

                                                <figcaption className='mt-5'>
                                                    <span className='block font-serif text-lg italic text-cream'>
                                                        {review.authorName}
                                                    </span>

                                                    <span className='block text-xs text-cream/50'>
                                                        {review.authorRole}
                                                    </span>
                                                </figcaption>
                                            </figure>
                                        </Reveal>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <aside id='book' aria-label={`Schedule time with ${advisor.name}`} className='scroll-mt-28 self-start lg:[@media(min-height:860px)]:sticky lg:[@media(min-height:860px)]:top-24'>
                        <Reveal y={30}>
                            <BookingPanel advisor={advisor} />
                        </Reveal>
                    </aside>
                </div>
            </section>

            {others.length > 0 && (
                <section aria-label='More advisors' className='relative border-t border-white/[0.06] py-24'>
                    <div className='shell'>
                        <div className='flex flex-wrap items-end justify-between gap-6'>
                            <div>
                                <Eyebrow align='left'>
                                    Still looking
                                </Eyebrow>

                                <SplitHeading text='More people *worth meeting*' className='mt-5 text-4xl font-normal tracking-[-0.04em] text-cream sm:text-5xl' />
                            </div>

                            <TextLink href='/advisors'>
                                Browse the full list
                            </TextLink>
                        </div>

                        <ul className='mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                            {others.map((other, index) => (
                                <Reveal as='li' key={other.slug} delay={index * 120}>
                                    <AdvisorCard advisor={other} compact />
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getAdvisorSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Advisor paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<AdvisorPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const advisor = slug ? await getAdvisorBySlug(slug) : null

    if (!advisor) return { notFound: true, revalidate: 30 }

    const [reviews, everyone] = await Promise.all([
        getAdvisorReviews(advisor.id).catch(() => []),
        getAdvisors().catch(() => [])
    ])

    const others = everyone
        .filter(other => other.slug !== advisor.slug)
        .sort((a, b) => Number(b.specialty === advisor.specialty) - Number(a.specialty === advisor.specialty))
        .slice(0, 3)

    return { props: { advisor, reviews, others }, revalidate: 60 }
}

export default AdvisorPage
